/**
 * How far back the duel's camera stands to show a whole `fit` arena, and where it aims
 * (client-arena:quality#4). The arena was fitted as the circle round its diagonal, as if it could
 * be seen from any side; seen from 40 degrees up it is a flat box, foreshortened, and that fit left
 * about half the frame empty. Now the box's own corners are fitted into the frame: the floor's
 * four and the four at the height of its tallest wall, with a margin, and the aim moves along the
 * floor until the near and far edges sit evenly in the frame.
 */

export interface FitView {
  /** Floor size in metres, across (x) and deep (z). */
  width: number;
  depth: number;
  /** Height of the tallest thing standing on the floor, whose far edge rises above it. */
  top: number;
  /** The camera's angle above the horizontal, radians. */
  elevation: number;
  /** The vertical field of view, radians. */
  fov: number;
  /** The frame's width over its height. */
  aspect: number;
  /** Room left round the arena: 1.06 leaves 6 % of each half of the frame. */
  margin: number;
}

export interface Fit {
  distance: number;
  /** Metres along z from the floor's middle to the point the camera aims at. */
  aimZ: number;
}

/** Where a point lands in the frame, -1…1 each way, from a camera aimed at (0, 0, aimZ). */
export function projectInFit(
  view: FitView,
  fit: Fit,
  point: { x: number; y: number; z: number },
): { x: number; y: number } {
  const sin = Math.sin(view.elevation);
  const cos = Math.cos(view.elevation);
  // The camera stands behind the near edge and above, looking along +z and down.
  const cy = sin * fit.distance;
  const cz = fit.aimZ - cos * fit.distance;
  const vx = point.x;
  const vy = point.y - cy;
  const vz = point.z - cz;
  const depth = vy * -sin + vz * cos;
  const up = vy * cos + vz * sin;
  const tanV = Math.tan(view.fov / 2);
  return { x: vx / (depth * tanV * view.aspect), y: up / (depth * tanV) };
}

const corners = (view: FitView) => {
  const out: { x: number; y: number; z: number }[] = [];
  for (const x of [-view.width / 2, view.width / 2])
    for (const z of [-view.depth / 2, view.depth / 2])
      for (const y of [0, view.top]) out.push({ x, y, z });
  return out;
};

/** How far past the margin the arena reaches at this fit: above 1, it does not fit. */
function reach(view: FitView, fit: Fit): { worst: number; low: number; high: number } {
  let worst = 0;
  let low = Infinity;
  let high = -Infinity;
  for (const corner of corners(view)) {
    const p = projectInFit(view, fit, corner);
    worst = Math.max(worst, Math.abs(p.x), Math.abs(p.y));
    low = Math.min(low, p.y);
    high = Math.max(high, p.y);
  }
  return { worst: worst * view.margin, low, high };
}

/** The nearest camera that shows the whole arena inside the margin, aimed to balance it. */
export function fitArena(view: FitView): Fit {
  const bisect = (lo: number, hi: number, tooLow: (x: number) => boolean) => {
    for (let i = 0; i < 48; i++) {
      const mid = (lo + hi) / 2;
      if (tooLow(mid)) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2;
  };
  // The aim that sits the near and far edges evenly in the frame from this far back: aiming
  // further along the floor moves the whole arena down the frame.
  const balanced = (distance: number) =>
    bisect(-view.depth / 2, view.depth / 2, (aimZ) => {
      const { low, high } = reach(view, { distance, aimZ });
      return low + high > 0;
    });
  // The nearest distance at which, aimed evenly, every corner is inside the margin. Further back
  // everything shrinks towards the middle, so the test only changes once.
  const radius = Math.hypot(view.width, view.depth, view.top) / 2;
  const far = (4 * radius) / Math.tan(view.fov / 2) / Math.min(1, view.aspect);
  const distance = bisect(
    0.01,
    far,
    (d) => reach(view, { distance: d, aimZ: balanced(d) }).worst > 1,
  );
  // Rounded up the last hair, so the corners are inside rather than on the margin.
  const safe = distance * (1 + 1e-9);
  return { distance: safe, aimZ: balanced(safe) };
}

/** The tallest thing standing on an arena's floor, in metres: walls, trees, rocks. Void is not. */
export function tallestStanding(map: {
  width: number;
  height: number;
  tile(x: number, y: number): { height: number };
}): number {
  let top = 0;
  for (let y = 0; y < map.height; y++)
    for (let x = 0; x < map.width; x++) {
      const height = map.tile(x, y).height;
      if (Number.isFinite(height)) top = Math.max(top, height);
    }
  return top;
}
